import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { BusinessUser } from "../../services";
import { Loader } from "../../common/components/loader";
import { removeUser, updateUser } from "../../slices/userSlice";
import {
  failureMessageConstant,
  keyConstants,
  successMessageConstant
} from "../../constants/index";
import PropTypes from "prop-types";
import Utils from "../../utility";
const { LOGO_UPLOAD_FAILED, MAX_ALPHA_NUM_CHARS, NAME_REQUIRED } =
  failureMessageConstant;
const { PROFILE_UPDATE_SUCCESS } = successMessageConstant;

const UpdateProfile = ({
  showUpdateProfile,
  setShowUpdateProfile,
  setProfileImagePreview
}) => {
  const user = useSelector((state) => state.user);
  const [company, setCompany] = useState(user.name);
  const [companyErr, setCompanyErr] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const { handleSubmit } = useForm();
  const dispatch = useDispatch();

  const { IS_REFRESH_TOKEN_LOADING } = keyConstants;

  /**
   * Sets the focus state to true.
   *
   * @return {void}
   */
  const handleFocus = () => {
    setIsFocused(true);
  };

  /**
   * Handles the blur event for the element.
   *
   * @param {none} none - No parameters.
   * @return {undefined} undefined - No return value.
   */
  const handleBlur = () => {
    setIsFocused(false);
  };
  /**
   * Handles the change event for the name input.
   *
   * @param {Event} e - The event object.
   * @return {void} - No return value.
   */
  const handleNameChange = (e) => {
    setCompanyErr("");
    const inputValue = e.target.value;
    if (inputValue.length <= 0) {
      setCompanyErr(NAME_REQUIRED);
    } else if (inputValue.length > 30) {
      setCompanyErr(MAX_ALPHA_NUM_CHARS);
      return;
    }
    // Regex pattern to allow only alphanumeric characters
    const alphanumericPattern = /^[a-zA-Z0-9\s]*$/;
    if (inputValue === "" || alphanumericPattern.test(inputValue)) {
      setCompany(inputValue);
    }
  };

  /**
   * Submits the form and updates the user profile.
   *
   * @return {Promise<void>} Resolves when the profile is successfully updated.
   */
  const submitHandler = async () => {
    const tokenStatus = await Utils.checkTokenExpireOrNot();
    if (tokenStatus) {
      if (tokenStatus === IS_REFRESH_TOKEN_LOADING) return;
      dispatch(removeUser({}));
      return;
    }
    if (companyErr) {
      return;
    }
    setLoading(true); // Set loading state to true

    let logoUrl = user?.logo;
    if (selectedFile) {
      // Check if there is a selected file
      const formData = new FormData(); // Create a new FormData object
      formData.append("businessId", user._id); // Append the "businessId" field to the FormData object with the value of user._id
      formData.append("logo", selectedFile); // Append the "logo" field to the FormData object with the value of selectedFile
      const [error, url] = await Utils.parseResponse(
        new BusinessUser().uploadLogo(formData) // Call the uploadLogo function from the BusinessUser class, passing in the formData object as an argument
      );
      if (error) {
        Utils.failureToastMessage(LOGO_UPLOAD_FAILED);
        setImagePreview(null);
        setSelectedFile(null); // clearing the selected file and preview
        setLoading(false); // Set loading state to false
        setShowUpdateProfile(false);
        return;
      }
      logoUrl = url;
    }

    const payload = {
      id: user?._id, // Get the user's ID
      name: company || user?.name, // Get the company name
      logo: logoUrl, // Set the logo to the image URL
      state: true // Set the state to true
    };

    await new BusinessUser().updateProfile(payload); // Call the updateProfile method of the BusinessUser class

    dispatch(
      updateUser({
        ...user,
        logo: logoUrl,
        name: company || user?.name
      })
    ); // Dispatch an action to update the user's logo and name
    Utils.successToastMessage(PROFILE_UPDATE_SUCCESS);
    setProfileImagePreview(imagePreview);
    setImagePreview(null);
    setSelectedFile(null);
    setLoading(false); // Set loading state to false
    setShowUpdateProfile(false); // close the dialog
  };

  /**
   * Selects a logo file and updates the image preview.
   *
   * @param {Event} event - The event object that triggered the function.
   * @return {void} This function does not return a value.
   */
  const selectLogoHandler = async (event) => {
    const file = event.target.files[0]; // Get the selected file from the event
    if (!file?.name.match(/\.(jpg|png|jpeg)$/)) {
      event.target.value = null; // Reset the file input value
      return; // Exit the function
    }
    if (file) {
      // If a file is selected
      const imageUrl = URL.createObjectURL(file); // Create a temporary URL for the selected file
      setImagePreview(imageUrl); // Set the image preview to the temporary URL
    }
    setSelectedFile(file); // Set the selected file
  };

  /**
   * Resets the image preview, selected file, company name, loading status,
   * and update profile flag.
   *
   * @return {void}
   */
  const handleCancel = () => {
    setImagePreview(null);
    setSelectedFile(null);
    setCompany(user?.name);
    setLoading(false);
    setShowUpdateProfile(false);
    setCompanyErr("");
  };
  return (
    <div>
      {showUpdateProfile && (
        <div className="fixed inset-0 z-30 h-screen w-full bg-black-00000050 px-3">
          <form onSubmit={handleSubmit(submitHandler)}>
            <div className="flex size-full items-center">
              <div className="m-auto mt-10 h-112.5 w-full rounded-[12px] bg-white px-3 pb-5.5 pt-19px text-black-000000 xs:max-w-391px md:max-w-457px md:px-5.5">
                <div className="mt-2 text-left font-MontserratSemiBold text-ft22-26.82-0.4 text-black-20">
                  Update Profile
                </div>
                <div className="mx-auto flex flex-col items-center pt-5">
                  <div className="size-135px rounded-full border border-grey-D9D9D9">
                    {imagePreview || user?.logo ? (
                      <img
                        className="size-full rounded-full object-cover"
                        src={
                          imagePreview ||
                          user?.logo ||
                          "/images/profile-image.svg"
                        }
                        alt="profile"
                      />
                    ) : (
                      <div className="flex size-135px items-center justify-center rounded-full font-Poppins text-ft48-16 font-medium text-black-000000">
                        {user?.name?.charAt(0)}
                      </div>
                    )}
                  </div>
                  <>
                    <input
                      type="file"
                      id="file-input"
                      className="hidden"
                      accept=".jpg, .jpeg, .png"
                      onChange={(event) => selectLogoHandler(event)}
                    />
                  </>
                  <label htmlFor="file-input">
                    <p className="mt-3 cursor-pointer font-MontserratMedium text-ft14-24.5 text-purple-6C36B0">
                      Upload logo
                    </p>
                  </label>
                </div>
                <div className="mt-21px">
                  <div className="font-MontserratMedium text-ft15-26.25 text-black-30">
                    Company Name
                  </div>
                  <div
                    className={`mt-1 flex h-49px w-full max-w-409px items-center justify-between rounded border ${companyErr ? "border-red-10" : "border-grey-C7C7C7"}  px-4 `}
                  >
                    <input
                      type="text"
                      placeholder="Enter Company Name"
                      className="w-full font-MontserratRegular text-ft15-26.25 outline-none"
                      value={isFocused ? company : company || user?.name}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      onChange={(e) => handleNameChange(e)}
                    />
                  </div>
                  {companyErr && (
                    <div className="mt-2 flex items-center gap-5px font-MontserratRegular text-ft13-15.85-0.26 text-red-10">
                      <img src="/images/info-circle.svg" alt="info"></img>
                      <p>{companyErr}</p>
                    </div>
                  )}
                  <div
                    className={`${
                      !companyErr ? "mt-8" : "mt-4"
                    } flex items-center justify-between gap-8`}
                  >
                    <button
                      onClick={handleCancel}
                      className="h-12 w-183px rounded border border-grey-C7C7C7 font-MontserratMedium text-ft15-18.29-0.3"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={`${
                        companyErr && "opacity-50"
                      } h-12 w-183px rounded bg-purple-6C36B0 font-MontserratMedium text-ft15-18.29-0.3 text-white`}
                    >
                      {loading ? <Loader /> : "Update"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};
UpdateProfile.propTypes = {
  showUpdateProfile: PropTypes.bool.isRequired, // Boolean indicating if the update profile section should be shown
  setShowUpdateProfile: PropTypes.func.isRequired, // Function to toggle the visibility of the update profile section
  setProfileImagePreview: PropTypes.func.isRequired // Function to set the preview of the profile image
};
export default UpdateProfile;
